import './App.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import VideoCard from './VideoCard';
import SkeletonReel from './components/SkeletonReel';
import { useScrollSnap } from './hooks/useScrollSnap';
import { useReelFeed } from './hooks/useReelFeed';
import { useReelWindow } from './hooks/useReelWindow';
import BroadcasterSelector from './components/BroadcasterSelector';
import CategorySelector from './components/CategorySelector';
import TagChip from './components/TagChip';
import { TRENDING_TAGS } from './components/discoveryOptions';
import FEATURE_FLAGS from './config/featureFlags';

/**
 * Uppercases the first letter of each chunk in a filter key to make it human friendly.
 *
 * @param {string} key - Raw filter identifier.
 * @returns {string} Readable label.
 */
const humanizeFilterLabel = (key) =>
  key
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const FEED_PRESETS_ENABLED = FEATURE_FLAGS.feedPresets;
const DISCOVERY_TAGS_ENABLED = FEATURE_FLAGS.discoveryTags;
const BROADCASTER_DISCOVERY_ENABLED = FEATURE_FLAGS.broadcasterDiscovery;
const CATEGORY_DISCOVERY_ENABLED = FEATURE_FLAGS.categoryDiscovery;

function App() {
  const scrollContainerRef = useRef(null);
  const discoveryControlsIdRef = useRef(null);
  if (!discoveryControlsIdRef.current) {
    discoveryControlsIdRef.current = `discovery-controls-${Math.random()
      .toString(36)
      .slice(2)}`;
  }
  const discoveryControlsId = discoveryControlsIdRef.current;

  const getIsDesktop = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return true;
    }
    return window.matchMedia('(min-width: 1024px)').matches;
  };

  const [isDesktop, setIsDesktop] = useState(() => getIsDesktop());
  const [isDiscoveryOpen, setIsDiscoveryOpen] = useState(() => getIsDesktop());
  const {
    reels,
    status,
    error,
    refresh,
    filters,
    filter,
    applyFilter,
    loadMore,
    hasMore,
    isLoadingMore,
    loadMoreError,
    criteria,
    setBroadcaster,
    setCategory,
    toggleTag,
    serializeCriteria,
    criteriaSignature,
  } = useReelFeed();
  useScrollSnap(scrollContainerRef);

  const {
    windowItems,
    queue,
    meta,
    beforeSpacerProps,
    afterSpacerProps,
    registerItem,
    mountedIndices,
  } = useReelWindow({ items: reels, containerRef: scrollContainerRef });

  const isLoading = status === 'loading';
  const hasError = status === 'error';

  const previousCriteriaSignatureRef = useRef(criteriaSignature);

  const filterOptions = useMemo(
    () =>
      FEED_PRESETS_ENABLED
        ? filters.map((value) => ({
            value,
            label: humanizeFilterLabel(value),
          }))
        : [],
    [filters],
  );

  const selectedTagIds = useMemo(
    () =>
      DISCOVERY_TAGS_ENABLED
        ? new Set((criteria?.tags ?? []).map((tag) => tag.id))
        : new Set(),
    [criteria?.tags],
  );

  const criteriaQuery = useMemo(
    () => serializeCriteria().toString(),
    [serializeCriteria],
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const { history, location } = window;

    if (!history || typeof history.replaceState !== 'function' || !location) {
      return;
    }

    const serializedCriteria = serializeCriteria();
    const nextBroadcaster = serializedCriteria.get('broadcaster');
    const nextCategory = serializedCriteria.get('category');

    const params = new URLSearchParams(location.search);
    let mutated = false;

    const applyParam = (key, value) => {
      const sanitizedValue = typeof value === 'string' ? value.trim() : '';
      if (sanitizedValue) {
        if (params.get(key) !== sanitizedValue) {
          params.set(key, sanitizedValue);
          mutated = true;
        }
        return;
      }

      if (params.has(key)) {
        params.delete(key);
        mutated = true;
      }
    };

    applyParam('broadcaster', nextBroadcaster);
    applyParam('category', nextCategory);

    if (!mutated) {
      return;
    }

    const nextQuery = params.toString();
    const currentSearch = typeof location.search === 'string' ? location.search : '';
    const nextSearch = nextQuery ? `?${nextQuery}` : '';

    if (nextSearch === currentSearch) {
      return;
    }

    const nextUrl = `${location.pathname}${nextSearch}${location.hash ?? ''}`;
    history.replaceState(history.state, '', nextUrl);
  }, [criteriaSignature, serializeCriteria]);

  useEffect(() => {
    if (previousCriteriaSignatureRef.current === criteriaSignature) {
      return;
    }
    previousCriteriaSignatureRef.current = criteriaSignature;
    const scroller = scrollContainerRef.current;
    if (scroller && typeof scroller.scrollTo === 'function') {
      scroller.scrollTo({
        top: 0,
        behavior: 'auto',
      });
    }
  }, [criteriaSignature]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQueryList = window.matchMedia('(min-width: 1024px)');
    const handleChange = (event) => {
      setIsDesktop(event.matches);
    };

    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', handleChange);
    } else {
      mediaQueryList.addListener(handleChange);
    }

    setIsDesktop(mediaQueryList.matches);

    return () => {
      if (typeof mediaQueryList.removeEventListener === 'function') {
        mediaQueryList.removeEventListener('change', handleChange);
      } else {
        mediaQueryList.removeListener(handleChange);
      }
    };
  }, []);

  useEffect(() => {
    if (isDesktop) {
      setIsDiscoveryOpen(true);
    }
  }, [isDesktop]);

  const handleBroadcasterChange = useCallback(
    (option) => {
      if (!BROADCASTER_DISCOVERY_ENABLED) {
        return;
      }
      setBroadcaster(option);
    },
    [setBroadcaster],
  );

  const handleCategoryChange = useCallback(
    (option) => {
      if (!CATEGORY_DISCOVERY_ENABLED) {
        return;
      }
      setCategory(option);
    },
    [setCategory],
  );

  const handleTagToggle = useCallback(
    (tag) => {
      if (!DISCOVERY_TAGS_ENABLED) {
        return;
      }
      toggleTag(tag);
    },
    [toggleTag],
  );

  const handleRefresh = useCallback(() => {
    refresh()
      .then(() => {
        const scroller = scrollContainerRef.current;
        if (scroller && typeof scroller.scrollTo === 'function') {
          scroller.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
      })
      .catch((err) => {
        console.error('Failed to refresh reels', err);
      });
  }, [refresh]);

  const handleLoadMore = useCallback(() => {
    if (!hasMore || isLoadingMore) {
      return;
    }

    loadMore().catch((err) => {
      if (err?.name === 'AbortError') {
        return;
      }
      console.error('Failed to load additional reels', err);
    });
  }, [hasMore, isLoadingMore, loadMore]);

  const handleFilterChipClick = useCallback(
    (value) => {
      if (!FEED_PRESETS_ENABLED || !value) {
        return;
      }
      if (value === filter) {
        handleRefresh();
        return;
      }
      applyFilter(value)
        .then(() => {
          const scroller = scrollContainerRef.current;
          if (scroller && typeof scroller.scrollTo === 'function') {
            scroller.scrollTo({
              top: 0,
              behavior: 'auto',
            });
          }
        })
        .catch((err) => {
          console.error('Failed to apply reels filter', err);
        });
    },
    [applyFilter, filter, handleRefresh],
  );

  const handleDiscoveryToggle = useCallback(() => {
    setIsDiscoveryOpen((prev) => !prev);
  }, []);

  const renderStatus = (() => {
    if (isLoading) {
      return (
        <p className="App-status sr-only" role="status" aria-live="polite">
          Loading clips...
        </p>
      );
    }
    if (hasError) {
      return (
        <p className="App-error" role="alert">
          {error?.message || 'Something went wrong while loading reels.'}
        </p>
      );
    }
    if (status === 'ready' && !reels.length) {
      return (
        <div className="App-emptyState" role="status" aria-live="polite">
          <div className="App-emptyStateIllustration" aria-hidden="true">
            <span className="App-emptyStateBadge" aria-hidden="true">
              🎬
            </span>
          </div>
          <p className="App-emptyStateText">No clips available for this filter yet.</p>
          <button
            type="button"
            className="App-emptyStateAction"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            Refresh feed
          </button>
        </div>
      );
    }
    return null;
  })();

  const queueAnnouncement = useMemo(() => {
    if (!meta.totalCount) {
      return 'No clips loaded.';
    }
    const activeNumber = meta.activeIndex + 1;
    const activeTitle = queue[0]?.item?.title
      ? queue[0].item.title
      : `Clip ${activeNumber}`;
    const upcoming = queue
      .slice(1)
      .map((entry) => entry.item?.title)
      .filter(Boolean);
    const upcomingText = upcoming.length
      ? `. Next: ${upcoming.join(' then ')}.`
      : '.';
    return `Clip ${activeNumber} of ${meta.totalCount}: ${activeTitle}${upcomingText}`;
  }, [meta.activeIndex, meta.totalCount, queue]);

  const queueLength = queue.length;
  const totalCount = Number.isFinite(meta.totalCount) ? meta.totalCount : reels.length;
  const activeIndex = Number.isFinite(meta.activeIndex) ? meta.activeIndex : 0;

  useEffect(() => {
    if (!hasMore || isLoadingMore || loadMoreError) {
      return;
    }

    if (!totalCount || totalCount <= 0) {
      return;
    }

    const projectedIndex = Math.max(activeIndex, 0) + queueLength;
    const remaining = totalCount - projectedIndex;

    if (remaining <= 2) {
      loadMore().catch((err) => {
        if (err?.name === 'AbortError') {
          return;
        }
        console.error('Failed to load additional reels', err);
      });
    }
  }, [
    activeIndex,
    hasMore,
    isLoadingMore,
    loadMore,
    loadMoreError,
    queueLength,
    totalCount,
  ]);

  return (
    <div className="App">
      <header className="App-header" role="banner">
        <nav className="App-nav" aria-label="Primary navigation">
          <div className="App-brand">
            <span className="App-brandTitle">Twitch Reels</span>
            <span className="App-brandBadge">Prototype</span>
          </div>
          <div
            className={`App-discovery${
              isDiscoveryOpen ? ' App-discovery--expanded' : ' App-discovery--collapsed'
            }`}
          >
            {!isDesktop && (
              <button
                type="button"
                className="App-discoveryToggle"
                aria-expanded={isDiscoveryOpen}
                aria-controls={discoveryControlsId}
                onClick={handleDiscoveryToggle}
              >
                {isDiscoveryOpen ? 'Hide discovery filters' : 'Show discovery filters'}
              </button>
            )}
            <div
              className="App-controls"
              role="group"
              aria-label="Feed controls"
              id={discoveryControlsId}
              hidden={!isDiscoveryOpen}
              data-state={isDiscoveryOpen ? 'open' : 'closed'}
            >
              {FEED_PRESETS_ENABLED ? (
                <div className="App-controlRow App-controlRow--presets">
                  <p className="App-controlLabel">Feed preset</p>
                  <div className="App-presetChips" role="list" aria-label="Feed presets">
                    {filterOptions.map(({ value, label }) => (
                      <div key={value} role="listitem" className="App-presetItem">
                        <TagChip
                          id={value}
                          label={label}
                          selected={filter === value}
                          onToggle={() => handleFilterChipClick(value)}
                          disabled={isLoading}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {BROADCASTER_DISCOVERY_ENABLED || CATEGORY_DISCOVERY_ENABLED ? (
                <div className="App-controlRow App-controlRow--selectors">
                  {BROADCASTER_DISCOVERY_ENABLED ? (
                    <BroadcasterSelector
                      value={criteria.broadcaster}
                      onChange={handleBroadcasterChange}
                      disabled={isLoading}
                    />
                  ) : null}
                  {CATEGORY_DISCOVERY_ENABLED ? (
                    <CategorySelector
                      value={criteria.category}
                      onChange={handleCategoryChange}
                      disabled={isLoading}
                    />
                  ) : null}
                </div>
              ) : null}
              {DISCOVERY_TAGS_ENABLED ? (
                <div className="App-controlRow App-controlRow--tags">
                  <p className="App-controlLabel">Discovery tags</p>
                  <div
                    className="App-tagChipGroup"
                    role="group"
                    aria-label="Toggle discovery tags"
                  >
                    {TRENDING_TAGS.map((tag) => (
                      <TagChip
                        key={tag.id}
                        id={tag.id}
                        label={tag.label}
                        selected={selectedTagIds.has(tag.id)}
                        onToggle={handleTagToggle}
                        disabled={isLoading}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className="App-refreshButton"
              onClick={handleRefresh}
              disabled={isLoading}
            >
              {isLoading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </nav>
        {renderStatus}
      </header>
      <main className="App-main">
        <p className="sr-only" aria-live="polite">
          {queueAnnouncement}
        </p>
        <div
          className="app__videos"
          ref={scrollContainerRef}
          role="list"
          aria-label="Twitch reels feed"
          aria-busy={isLoading}
          data-active-index={meta.activeIndex}
          data-total-count={meta.totalCount}
          data-mounted-indices={mountedIndices.join(',') || 'none'}
          data-criteria-query={criteriaQuery || 'none'}
        >
          <div
            className="app__windowSpacer app__windowSpacer--before"
            {...beforeSpacerProps}
          />
          {isLoading && !windowItems.length && (
            <>
              {Array.from({ length: 3 }).map((_, index) => (
                <SkeletonReel key={`skeleton-${index}`} />
              ))}
            </>
          )}
          {windowItems.map(({ item, index, slotKey }) => (
            <VideoCard
              {...item}
              index={index}
              key={`window-slot-${slotKey}`}
              ref={(node) => registerItem(index, node)}
              aria-setsize={meta.totalCount || undefined}
              aria-posinset={index + 1}
              data-window-slot={slotKey}
            />
          ))}
          <div
            className="app__windowSpacer app__windowSpacer--after"
            {...afterSpacerProps}
          />
        </div>
        {hasMore && (
          <div className="App-loadMore" role="region" aria-live="polite">
            <button
              type="button"
              className="App-refreshButton App-loadMoreButton"
              onClick={handleLoadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? 'Loading more…' : 'Load more'}
            </button>
            {loadMoreError && (
              <p className="App-loadMoreError" role="alert">
                {loadMoreError.message}
              </p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
